import { createContext, memo, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Search } from "lucide-react";
import { documentRelativeTime } from "@/lib/safeDate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkDocumentDialog } from "./LinkDocumentDialog";
import { useKnowledgeBaseList } from "@/contexts/KnowledgeBaseContext";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { sourcesService, type Source } from "@/services/sourcesService";
import { documentTypes } from "@/components/rightSidebarDocumentTypes";

const AttachmentPanelSourcesContext = createContext<Source[]>([]);

export function useAttachmentPanelSources(): Source[] {
  return useContext(AttachmentPanelSourcesContext);
}

interface AttachmentPanelProps {
  onOpenDocument?: (doc: any) => Promise<void>;
  children: ReactNode;
}

export const AttachmentPanel = memo(function AttachmentPanel({ onOpenDocument, children }: AttachmentPanelProps) {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [isOpenDocDialogOpen, setIsOpenDocDialogOpen] = useState(false);
  const [linkingDocumentId, setLinkingDocumentId] = useState<string | null>(null);
  const [docSearchQuery, setDocSearchQuery] = useState("");

  // Fetch sources for library tree (workspace-scoped)
  const { data: sources = [] } = useQuery<Source[]>({
    queryKey: ["/api/sources", user?.id, currentWorkspaceId ?? null],
    queryFn: async () => {
      if (!user) return [];
      return await sourcesService.getSources(undefined, currentWorkspaceId ?? null);
    },
    enabled: !!user,
    staleTime: 30000,
  });

  const {
    items: knowledgeItems,
    isLoading: knowledgeItemsLoading,
    hasNextPage: knowledgeHasNextPage,
    isFetchingNextPage: knowledgeFetchingNextPage,
    fetchNextPage: fetchNextKnowledgePage,
  } = useKnowledgeBaseList({ enabled: isOpenDocDialogOpen });

  const filteredDocuments = useMemo(
    () =>
      knowledgeItems.filter((doc: any) =>
        doc.title?.toLowerCase().includes(docSearchQuery.toLowerCase())
      ),
    [knowledgeItems, docSearchQuery]
  );

  const getDocumentTypeInfo = useCallback((type: string) => {
    return documentTypes.find((dt) => dt.value === type) || documentTypes[0];
  }, []);

  const handleOpenExistingDocument = useCallback(
    async (doc: any) => {
      if (!onOpenDocument) return;
      await onOpenDocument(doc);
      setIsOpenDocDialogOpen(false);
      setDocSearchQuery("");
    },
    [onOpenDocument]
  );

  return (
    <>
      <AttachmentPanelSourcesContext.Provider value={sources}>{children}</AttachmentPanelSourcesContext.Provider>

      {linkingDocumentId && (
        <LinkDocumentDialog
          open={isLinkDialogOpen}
          onClose={() => {
            setIsLinkDialogOpen(false);
            setLinkingDocumentId(null);
          }}
          documentId={linkingDocumentId}
        />
      )}

      <Dialog open={isOpenDocDialogOpen} onOpenChange={setIsOpenDocDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Open Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={docSearchQuery}
                onChange={(e) => setDocSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search-documents"
              />
            </div>
            <ScrollArea className="h-[300px]">
              {knowledgeItemsLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  <div className="h-8 w-8 mx-auto mb-2 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  <p className="text-sm">Loading documents...</p>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No documents found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredDocuments.map((doc: any) => {
                    const typeInfo = getDocumentTypeInfo(doc.type);
                    const TypeIcon = typeInfo.icon;
                    return (
                      <Card
                        key={doc.id}
                        className="p-3 cursor-pointer hover-elevate"
                        onClick={() => handleOpenExistingDocument(doc)}
                        data-testid={`open-doc-${doc.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 rounded-md flex-shrink-0" style={{ backgroundColor: `${typeInfo.color}15` }}>
                            <TypeIcon className="h-3 w-3" style={{ color: typeInfo.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{doc.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {documentRelativeTime(doc.updatedAt, doc.createdAt)}
                            </p>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                  {knowledgeHasNextPage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => fetchNextKnowledgePage()}
                      disabled={knowledgeFetchingNextPage}
                    >
                      {knowledgeFetchingNextPage ? "Loading..." : "Load more"}
                    </Button>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});
